import { FORMATION_LABELS, TITLE_FADE_MS } from "../config/constants.ts";
import type { AppSettings, FormationId, PrivacyFrameState } from "../utils/types.ts";
import type { DemoStep } from "../demo/DemoSequence.ts";

export class UIController {
  readonly landing = el("landing");
  readonly errorOverlay = el("error-overlay");
  readonly errorBody = el("error-body");
  readonly brandHero = el("brand-hero");
  readonly brandMini = el("brand-mini");
  readonly cameraPanel = el("camera-panel");
  readonly cameraCanvas = el("camera-processed") as HTMLCanvasElement;
  readonly cameraCover = el("camera-safe-cover");
  readonly cameraMessage = el("camera-safe-message");
  readonly gestureLabel = el("gesture-label");
  readonly handConfidence = el("hand-confidence");
  readonly privacyChip = el("privacy-chip");
  readonly formationToast = el("formation-toast");
  readonly gestureFlash = el("gesture-flash");
  readonly visualTestBanner = el("visual-test-banner");
  readonly calibrationPanel = el("calibration-panel");
  readonly calibrationForm = el("calibration-form") as HTMLFormElement;
  readonly calibrationStats = el("calibration-stats");
  readonly calibrationClose = el("calibration-close") as HTMLButtonElement;
  readonly btnVisualTest = el("btn-visual-test") as HTMLButtonElement;
  readonly btnCalibrate = el("btn-calibrate") as HTMLButtonElement;
  readonly demoCoach = el("demo-coach");
  readonly fpsReadout = el("fps-readout");
  readonly debugReadout = el("debug-readout");
  readonly recordingHud = el("recording-hud");
  readonly recTimer = el("rec-timer");
  readonly countdown = el("countdown-overlay");
  readonly countdownNumber = el("countdown-number");
  readonly demoBanner = el("demo-mode-banner");
  readonly settingsPanel = el("settings-panel");
  readonly settingsForm = el("settings-form") as HTMLFormElement;
  readonly controlDock = el("control-dock");
  readonly btnEnable = el("btn-enable-camera") as HTMLButtonElement;
  readonly btnCameraOff = el("btn-camera-off") as HTMLButtonElement;
  readonly btnReset = el("btn-reset") as HTMLButtonElement;
  readonly btnRecord = el("btn-record") as HTMLButtonElement;
  readonly btnRecord15 = el("btn-record-15") as HTMLButtonElement;
  readonly btnRecord30 = el("btn-record-30") as HTMLButtonElement;
  readonly btnDownload = el("btn-download") as HTMLButtonElement;
  readonly btnDemo = el("btn-demo-seq") as HTMLButtonElement;
  readonly btnDemo30 = el("btn-demo-30") as HTMLButtonElement;
  readonly cameraLive = el("camera-live-dot");
  readonly btnSound = el("btn-sound") as HTMLButtonElement;
  readonly btnSettings = el("btn-settings") as HTMLButtonElement;
  readonly btnPresentation = el("btn-presentation") as HTMLButtonElement;
  readonly volumeSlider = el("volume-slider") as HTMLInputElement;
  readonly landingEnable = el("landing-enable") as HTMLButtonElement;
  readonly landingDemo = el("landing-demo") as HTMLButtonElement;
  readonly errorDismiss = el("error-dismiss") as HTMLButtonElement;
  readonly settingsClose = el("settings-close") as HTMLButtonElement;
  readonly cameraCollapse = el("camera-collapse") as HTMLButtonElement;
  private toastTimer = 0;
  private flashTimer = 0;

  fadeTitle(): void {
    window.setTimeout(() => {
      this.brandHero.classList.add("is-faded");
      this.brandMini.hidden = false;
    }, TITLE_FADE_MS);
  }

  hideLanding(): void {
    this.landing.hidden = true;
  }

  showError(message: string): void {
    this.errorBody.textContent = message;
    this.errorOverlay.hidden = false;
  }

  hideError(): void {
    this.errorOverlay.hidden = true;
  }

  setCameraEnabled(on: boolean): void {
    document.body.classList.toggle("is-camera-on", on);
    this.btnEnable.hidden = on;
    this.btnCameraOff.hidden = !on;
    this.demoBanner.hidden = on;
    this.cameraLive.hidden = !on;
  }

  setCameraCover(visible: boolean, message: string): void {
    this.cameraCover.hidden = !visible;
    this.cameraMessage.textContent = message;
  }

  setGesture(label: string, confidence: number): void {
    this.gestureLabel.textContent = label;
    this.handConfidence.textContent =
      confidence > 0 ? `TRACK ${Math.round(confidence * 100)}%` : "TRACK —";
  }

  setPrivacy(state: PrivacyFrameState): void {
    this.privacyChip.textContent = state.fallbackActive
      ? "PRIVACY FALLBACK ACTIVE"
      : "FACE CONCEALED";
    this.privacyChip.classList.toggle("meta-pill--ok", !state.fallbackActive);
  }

  flashGesture(label: string): void {
    this.gestureFlash.hidden = false;
    const inner = this.gestureFlash.querySelector(".gesture-flash-inner");
    if (inner) inner.textContent = label;
    else this.gestureFlash.textContent = label;
    this.gestureFlash.classList.add("is-on");
    window.clearTimeout(this.flashTimer);
    this.flashTimer = window.setTimeout(() => {
      this.gestureFlash.classList.remove("is-on");
    }, 1100);
  }

  setVisualTest(on: boolean, caption?: string): void {
    this.visualTestBanner.hidden = !on;
    if (caption) this.visualTestBanner.textContent = caption;
    this.btnVisualTest.setAttribute("aria-pressed", String(on));
  }

  toggleCalibration(force?: boolean): void {
    const show = force ?? this.calibrationPanel.hidden;
    this.calibrationPanel.hidden = !show;
    this.btnCalibrate.setAttribute("aria-expanded", String(show));
  }

  toastFormation(id: FormationId): void {
    this.formationToast.hidden = false;
    this.formationToast.textContent = FORMATION_LABELS[id];
    window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => {
      this.formationToast.hidden = true;
    }, 1600);
  }

  setDemoCoach(step: DemoStep | null): void {
    if (!step) {
      this.demoCoach.hidden = true;
      return;
    }
    this.demoCoach.hidden = false;
    this.demoCoach.textContent = `NEXT: ${step.label} — ${step.hint}`;
  }

  setFps(fps: number, show: boolean): void {
    this.fpsReadout.hidden = !show;
    if (show) this.fpsReadout.textContent = `FPS ${fps.toFixed(0)}`;
  }

  setDebug(text: string | null): void {
    this.debugReadout.hidden = !text;
    if (text) this.debugReadout.textContent = text;
  }

  setRecording(on: boolean, seconds = 0): void {
    this.recordingHud.hidden = !on;
    this.btnRecord.textContent = on ? "Stop Recording" : "Start Recording";
    const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
    const ss = String(Math.floor(seconds % 60)).padStart(2, "0");
    this.recTimer.textContent = `${mm}:${ss}`;
  }

  setCountdown(n: number): void {
    this.countdown.hidden = n <= 0;
    this.countdownNumber.textContent = String(n);
  }

  setDownloadEnabled(on: boolean): void {
    this.btnDownload.disabled = !on;
  }

  setSound(on: boolean, volume: number): void {
    this.btnSound.textContent = on ? "Sound On" : "Sound Off";
    this.btnSound.setAttribute("aria-pressed", String(on));
    this.volumeSlider.value = String(volume);
  }

  setPresentation(on: boolean): void {
    document.body.classList.toggle("is-presentation", on);
    this.btnPresentation.setAttribute("aria-pressed", String(on));
  }

  applySettings(settings: AppSettings): void {
    this.cameraPanel.classList.remove(
      "camera-panel--small",
      "camera-panel--medium",
      "camera-panel--large",
      "camera-panel--bottom-right",
      "camera-panel--bottom-left",
      "camera-panel--top-right",
      "camera-panel--top-left",
    );
    this.cameraPanel.classList.add(
      `camera-panel--${settings.cameraPanelSize}`,
      `camera-panel--${settings.cameraPanelPosition}`,
    );
    this.setPresentation(settings.presentationMode);
    this.setSound(settings.soundEnabled, settings.volume);
    const form = this.settingsForm;
    (form.elements.namedItem("privacyMode") as HTMLSelectElement).value = settings.privacyMode;
    (form.elements.namedItem("concealmentType") as HTMLSelectElement).value =
      settings.concealmentType;
    (form.elements.namedItem("concealmentStrength") as HTMLInputElement).value = String(
      settings.concealmentStrength,
    );
    (form.elements.namedItem("cameraPanelSize") as HTMLSelectElement).value =
      settings.cameraPanelSize;
    (form.elements.namedItem("cameraPanelPosition") as HTMLSelectElement).value =
      settings.cameraPanelPosition;
    (form.elements.namedItem("particleQuality") as HTMLSelectElement).value =
      settings.particleQuality;
    (form.elements.namedItem("colourTheme") as HTMLSelectElement).value = settings.colourTheme;
    (form.elements.namedItem("gestureSensitivity") as HTMLInputElement).value = String(
      settings.gestureSensitivity,
    );
    (form.elements.namedItem("gestureSmoothing") as HTMLInputElement).value = String(
      settings.gestureSmoothing,
    );
    (form.elements.namedItem("soundEnabled") as HTMLInputElement).checked = settings.soundEnabled;
    (form.elements.namedItem("volume") as HTMLInputElement).value = String(settings.volume);
    (form.elements.namedItem("showFps") as HTMLInputElement).checked = settings.showFps;
    (form.elements.namedItem("debugMode") as HTMLInputElement).checked = settings.debugMode;
    (form.elements.namedItem("presentationMode") as HTMLInputElement).checked =
      settings.presentationMode;
    (form.elements.namedItem("mirrorCamera") as HTMLInputElement).checked = settings.mirrorCamera;
  }

  toggleSettings(force?: boolean): void {
    const show = force ?? this.settingsPanel.hidden;
    this.settingsPanel.hidden = !show;
    this.btnSettings.setAttribute("aria-expanded", String(show));
  }

  toggleCameraCollapse(): void {
    this.cameraPanel.classList.toggle("is-collapsed");
    const collapsed = this.cameraPanel.classList.contains("is-collapsed");
    this.cameraCollapse.setAttribute(
      "aria-label",
      collapsed ? "Expand camera panel" : "Collapse camera panel",
    );
  }

  cameraPanelRect(): DOMRect {
    return this.cameraPanel.getBoundingClientRect();
  }

  processedCanvasRect(): DOMRect {
    return this.cameraCanvas.getBoundingClientRect();
  }
}

function el(id: string): HTMLElement {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing #${id}`);
  return node;
}
